import * as React from "react";
import { css } from "@emotion/core";
import {
  Dialog,
  Button,
  Spinner,
  NonIdealState,
  Icon,
  InputGroup,
} from "@blueprintjs/core";
import { useQuery } from "@tanstack/react-query";
import ApiAdmin, { IOrderType } from "../../../../api/admin";
import { useUpdateFolderOrderTypesMutation } from "../../../../hooks";
import { compareOrderTypesByNameThenSiteLabel } from "./folderOrderTypesDialogSort";

type OrderTypeWithAssignment = IOrderType & { assignment_id: number };

function hasAssignmentId(
  orderType: IOrderType
): orderType is OrderTypeWithAssignment {
  return orderType.assignment_id !== undefined;
}

interface FolderOrderTypesDialogProps {
  folder: { id: number; label: string };
  organizationId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const FolderOrderTypesDialog: React.FC<FolderOrderTypesDialogProps> = ({
  folder,
  organizationId,
  isOpen,
  onClose,
}) => {
  const [selectedOrderTypeIds, setSelectedOrderTypeIds] = React.useState<
    Set<number>
  >(new Set());
  const [filterQuery, setFilterQuery] = React.useState<string>("");

  const {
    data: folderOrderTypesData,
    isLoading: isLoadingFolderOrderTypes,
    error: folderOrderTypesError,
  } = useQuery({
    queryKey: ["folderOrderTypes", folder.id],
    queryFn: () =>
      ApiAdmin.getFolderOrderTypes(organizationId, folder.id),
    enabled: isOpen && !!organizationId,
  });

  const { data: orgOrderTypesData, isLoading: isLoadingOrgOrderTypes } =
    useQuery({
      queryKey: ["orderTypes", organizationId],
      queryFn: () =>
        ApiAdmin.getOrderTypesForOrganization(organizationId),
      enabled: isOpen && !!organizationId,
    });

  React.useEffect(() => {
    if (folderOrderTypesData?.organization_order_type_ids) {
      setSelectedOrderTypeIds(
        new Set(folderOrderTypesData.organization_order_type_ids)
      );
    }
  }, [folderOrderTypesData]);

  const updateMutation = useUpdateFolderOrderTypesMutation(organizationId, {
    onSuccess: () => onClose(),
    onError: (error) => {
      console.error("Failed to update folder order types:", error);
    },
  });

  const handleOrderTypeToggle = (orderTypeId: number, checked: boolean) => {
    setSelectedOrderTypeIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(orderTypeId);
      } else {
        next.delete(orderTypeId);
      }
      return next;
    });
  };

  const handleSave = () => {
    const orderTypeIds = Array.from(selectedOrderTypeIds);
    updateMutation.mutate({
      folderId: folder.id,
      organizationOrderTypeIds: orderTypeIds,
    });
  };

  const handleCancel = () => {
    onClose();
  };

  const availableOrderTypes = React.useMemo(() => {
    if (!orgOrderTypesData?.order_types) return [];
    return orgOrderTypesData.order_types
      .filter((ot) => !ot.deleted_at)
      .sort(compareOrderTypesByNameThenSiteLabel);
  }, [orgOrderTypesData?.order_types]);

  const filteredOrderTypes = React.useMemo(() => {
    if (!filterQuery.trim()) return availableOrderTypes;
    const query = filterQuery.toLowerCase();
    return availableOrderTypes.filter(
      (orderType) =>
        orderType.name.toLowerCase().includes(query) ||
        (orderType.description &&
          orderType.description.toLowerCase().includes(query))
    );
  }, [availableOrderTypes, filterQuery]);

  const isLoading = isLoadingFolderOrderTypes || isLoadingOrgOrderTypes;
  const hasError = !!folderOrderTypesError;
  const isPending = updateMutation.isPending;

  return (
    <Dialog
      className="bp6-dark"
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Order Types"
      css={css`
        width: 500px;
        max-width: 90vw;
      `}
    >
      <div
        css={css`
          height: 50vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 2rem 2rem 0 1rem;
            flex-shrink: 0;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <Icon icon="folder-open" size={32} color="var(--neutral-3)" />
            <h2
              css={css`
                color: var(--neutral-3);
                margin: 1rem 0;
              `}
            >
              {folder.label}
            </h2>
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            flex: 1;
            min-height: 0;
            overflow: hidden;
          `}
        >
          {isLoading ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 2rem;
              `}
            >
              <Spinner size={40} />
            </div>
          ) : hasError ? (
            <NonIdealState
              icon="error"
              title="Error Loading Order Types"
              description={
                folderOrderTypesError instanceof Error
                  ? folderOrderTypesError.message
                  : "Failed to load folder order types"
              }
            />
          ) : (
            <>
              <div
                css={css`
                  padding: 0 1rem 1rem 1rem;
                  border-bottom: 1px solid var(--neutral-5);
                  flex-shrink: 0;
                `}
              >
                <InputGroup
                  placeholder="Filter order types..."
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  leftIcon="search"
                />
              </div>
              <div
                css={css`
                  overflow-y: auto;
                  flex: 1;
                  padding: 0.5rem;
                `}
              >
                {filteredOrderTypes.length === 0 ? (
                  <NonIdealState
                    icon="cube"
                    title="No Order Types Available"
                    description="No order types are assigned to this organization"
                  />
                ) : (
                  filteredOrderTypes.filter(hasAssignmentId).map((orderType) => (
                    <div
                      key={orderType.assignment_id}
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        padding: 0.5rem;
                        &:hover {
                          background-color: var(--neutral-6);
                        }
                      `}
                      onClick={() =>
                        handleOrderTypeToggle(
                          orderType.assignment_id,
                          !selectedOrderTypeIds.has(orderType.assignment_id)
                        )
                      }
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          width: 18px;
                          height: 18px;
                          background-color: var(--neutral-6);
                          border: 1px solid var(--neutral-5);
                          border-radius: 3px;
                        `}
                      >
                        <Icon
                          icon="tick"
                          color={
                            selectedOrderTypeIds.has(orderType.assignment_id)
                              ? "var(--neutral-3)"
                              : "var(--neutral-6)"
                          }
                          size={14}
                        />
                      </div>

                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          flex: 1;
                        `}
                      >
                        <span>
                          {orderType.name}
                          {orderType.site_label && (
                            <span
                              css={css`
                                color: var(--neutral-3);
                              `}
                            >
                              {` - ${orderType.site_label}`}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {updateMutation.isError && (
                <div
                  css={css`
                    color: var(--danger);
                    font-size: 0.875rem;
                    padding: 0.5rem;
                  `}
                >
                  {updateMutation.error instanceof Error
                    ? updateMutation.error.message
                    : "Failed to save order types"}
                </div>
              )}
            </>
          )}
        </div>
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            border-top: 1px solid var(--neutral-5);
            flex-shrink: 0;
          `}
        >
          <Button text="Cancel" disabled={isPending} onClick={handleCancel} />
          <Button
            text={isPending ? "Saving..." : "Save"}
            intent="primary"
            disabled={isLoading || hasError || isPending}
            onClick={handleSave}
          />
        </div>
      </div>
    </Dialog>
  );
};
